import { z } from "zod";

/**
 * Sovereign Type Drift Defense (V2.1.0)
 * Purpose: Runtime validation to prevent 'Zombie App' states from corrupted or unexpected DB data.
 */

// 1. Core Fragments
export const ServiceOfferingSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    name_ar: z.string(),
    price: z.number(),
    duration_minutes: z.number().optional(),
    buffer_minutes: z.number().optional(),
    section: z.string().optional(),
    is_session_based: z.boolean().optional(),
    sessions_count: z.number().int().positive().optional(),
    is_starting_price: z.boolean().optional(),
    item_type: z.enum(["service", "product"]).optional(),
    show_on_storefront: z.boolean().optional(),
  })
  .refine(
    (data) => {
      if (data.is_session_based) {
        return data.sessions_count !== undefined && data.sessions_count > 0;
      }
      return true;
    },
    {
      message:
        "Sessions count must be a positive integer if service is session-based",
      path: ["sessions_count"],
    },
  );

export const DayScheduleSchema = z.object({
  isOpen: z.boolean(),
  start: z.string(),
  end: z.string(),
});

// Lenient variant for reading `businesses.schedule` off the network: a single
// incomplete/malformed day (e.g. a partial manual edit) should degrade to the
// same 09:00-17:00 default the UI already falls back to, not hard-fail the
// whole business fetch the way a missing client_name on a booking should.
const LenientDayScheduleSchema = z.object({
  isOpen: z
    .boolean()
    .nullable()
    .optional()
    .transform((val) => val ?? false),
  start: z
    .string()
    .nullable()
    .optional()
    .transform((val) => val || "09:00"),
  end: z
    .string()
    .nullable()
    .optional()
    .transform((val) => val || "17:00"),
});

// Both current fetch sites (BusinessDashboard.fetchStaff,
// BookingForm.fetchActiveWorkers) select only `id, name, role, is_active`,
// so only those are required here -- business_id/user_id/created_at/
// updated_at/deactivated_at are validated when present but not demanded,
// since a query that doesn't select a column will never produce it.
export const StaffAssignmentSchema = z.object({
  id: z.string().uuid(),
  business_id: z.string().uuid().optional(),
  // Unlike bookings, most `business_staff` rows have no linked auth user --
  // regular stylists don't get login access, only admins do (see
  // TeamShiftsPanel's staff-insert, which omits user_id entirely). The
  // column is nullable in the DB; StaffAssignment's `user_id: string` in
  // types.ts is stale and shouldn't be mirrored here.
  user_id: z.string().uuid().nullable().optional(),
  role: z.enum(["admin", "staff"]),
  name: z
    .string()
    .nullable()
    .optional()
    .transform((val) => val ?? undefined),
  is_active: z
    .boolean()
    .nullable()
    .optional()
    .transform((val) => val ?? true),
  deactivated_at: z
    .string()
    .nullable()
    .optional()
    .transform((val) => val ?? undefined),
  created_at: z.string().optional(),
  updated_at: z
    .string()
    .nullable()
    .optional()
    .transform((val) => val ?? undefined),
});

// 2. Main Schemas
export const BookingSchema = z.object({
  id: z.string().uuid(),
  business_id: z.string().uuid(),
  created_at: z.string(),
  client_name: z.string().min(1),
  phone_number: z.string().min(1),
  service_type: z.string(),
  services_booked: z
    .array(ServiceOfferingSchema)
    .nullable()
    .transform((val) => val ?? undefined),
  total_price: z
    .number()
    .nullable()
    .transform((val) => val ?? undefined),
  requested_date: z.string(),
  requested_time: z.string(),
  status: z.enum(["confirmed", "completed", "cancelled", "missed"]),
  staff_notes: z
    .string()
    .nullable()
    .transform((val) => val ?? undefined),
  source: z
    .enum(["online", "phone", "walk-in"])
    .nullable()
    .transform((val) => val ?? undefined),
  cancellation_reason: z
    .string()
    .nullable()
    .transform((val) => val ?? undefined),
  reminder_sent: z
    .boolean()
    .nullable()
    .transform((val) => val ?? undefined),
  language: z
    .enum(["ar", "en"])
    .nullable()
    .transform((val) => val ?? undefined),
  worker_id: z.string().uuid().nullable().optional(),
  duration_minutes: z
    .number()
    .nullable()
    .optional()
    .transform((val) => val ?? undefined),
  idempotency_key: z.string().nullable().optional(),
  deleted_at: z.string().nullable().optional(),
  ticket_state: z
    .enum([
      "unpaid",
      "pending_verification",
      "deposit_verified",
      "paid",
      "replaced_refund_due",
      "converted_to_credit",
      "completed",
    ])
    .nullable()
    .optional()
    .transform((val) => val ?? undefined),
  noshow_swept_at: z
    .string()
    .nullable()
    .optional()
    .transform((val) => val ?? undefined),
  payment_proof_path: z
    .string()
    .nullable()
    .optional()
    .transform((val) => val ?? undefined),
  deposit_reference: z
    .string()
    .nullable()
    .optional()
    .transform((val) => val ?? undefined),
  deposit_verified_at: z
    .string()
    .nullable()
    .optional()
    .transform((val) => val ?? undefined),
  deposit_verified_by_worker_id: z
    .string()
    .nullable()
    .optional()
    .transform((val) => val ?? undefined),
  payment_method: z
    .enum(["cash", "cliq", "card", "credit", "split", "pos_card"])
    .nullable()
    .optional()
    .transform((val) => val ?? undefined),
  paid_at: z
    .string()
    .nullable()
    .optional()
    .transform((val) => val ?? undefined),
  credit_applied: z
    .number()
    .nullable()
    .optional()
    .transform((val) => val ?? undefined),
  deposit_paid_amount: z
    .number()
    .nullable()
    .optional()
    .transform((val) => val ?? undefined),
  cancelled_by: z
    .enum(["client", "business"])
    .nullable()
    .optional()
    .transform((val) => val ?? undefined),
  refund_due_amount: z
    .number()
    .nullable()
    .optional()
    .transform((val) => val ?? undefined),
  refund_due_reason: z
    .string()
    .nullable()
    .optional()
    .transform((val) => val ?? undefined),
  refund_settled_at: z
    .string()
    .nullable()
    .optional()
    .transform((val) => val ?? undefined),
  settled_by_worker_id: z
    .string()
    .nullable()
    .optional()
    .transform((val) => val ?? undefined),
  cancellation_token: z
    .string()
    .uuid()
    .nullable()
    .optional()
    .transform((val) => val ?? undefined),
});

export const BusinessSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1),
  slug: z.string().min(1),
  plan_tier: z
    .enum(["starter", "standard", "premium"])
    .optional()
    .nullable()
    .transform((val) => val ?? undefined),
  logo_url: z
    .string()
    .url()
    .nullable()
    .or(z.string().length(0))
    .transform((val) => val ?? undefined),
  primary_color: z
    .string()
    .nullable()
    .transform((val) => val ?? undefined),
  phone_number: z
    .string()
    .optional()
    .nullable()
    .transform((val) => val ?? undefined),
  service_offerings: z
    .array(ServiceOfferingSchema)
    .nullable()
    .transform((val) => val ?? undefined),
  is_active: z
    .boolean()
    .nullable()
    .transform((val) => val ?? undefined),
  schedule: z
    .record(z.string(), LenientDayScheduleSchema)
    .nullable()
    .transform((val) =>
      val
        ? (val as Record<string, z.infer<typeof LenientDayScheduleSchema>>)
        : undefined,
    ),
  created_at: z
    .string()
    .nullable()
    .transform((val) => val ?? undefined),
  whatsapp_enabled: z
    .boolean()
    .nullable()
    .transform((val) => val ?? undefined),
  whatsapp_config_id: z
    .string()
    .optional()
    .nullable()
    .transform((val) => val ?? undefined),
  preferred_language: z
    .enum(["ar", "en"])
    .nullable()
    .transform((val) => val ?? undefined),
  location_link: z
    .string()
    .nullable()
    .transform((val) => val ?? undefined),
  cancellation_window_hours: z
    .number()
    .int()
    .min(0, "Cancellation window cannot be negative")
    .max(168, "Cancellation window cannot exceed 168 hours (7 days)")
    .optional()
    .nullable()
    .transform((val) => val ?? 2),
  noshow_grace_minutes: z
    .number()
    .int()
    .min(0, "Grace period cannot be negative")
    .max(180, "Grace period cannot exceed 180 minutes (3 hours)")
    .optional()
    .nullable()
    .transform((val) => val ?? 30),
  min_lead_minutes: z
    .number()
    .int()
    .min(0, "Notice cannot be negative")
    .max(1440, "Notice cannot exceed 1440 minutes (24 hours)")
    .optional()
    .nullable()
    .transform((val) => val ?? 15),
  credit_validity_days: z
    .number()
    .int()
    .min(1, "Credit validity must be at least 1 day")
    .max(3650, "Credit validity cannot exceed 3650 days (10 years)")
    .optional()
    .nullable()
    .transform((val) => val ?? 180),
  // Null means "use the platform default" (see send-whatsapp-v2's
  // DEFAULT_DAILY_SPEND_CAP_JOD) -- unlike the fields above, this one must
  // NOT be coerced to a fixed number here, or every business would silently
  // pin to whatever the default was at save time instead of tracking it.
  daily_whatsapp_spend_cap_jod: z
    .number()
    .min(0, "Spend cap cannot be negative")
    .optional()
    .nullable()
    .transform((val) => val ?? undefined),
  require_upfront_payment: z
    .boolean()
    .optional()
    .nullable()
    .transform((val) => val ?? false),
  upfront_payment_mode: z
    .enum(["fixed_deposit", "full_prepayment"])
    .optional()
    .nullable()
    .transform((val) => val ?? "fixed_deposit"),
  upfront_deposit_amount: z
    .number()
    .min(0, "Deposit amount cannot be negative")
    .max(1000, "Deposit exceeds maximum allowed threshold (1000 JOD)")
    .optional()
    .nullable()
    .transform((val) => val ?? 5),
  cliq_alias: z
    .string()
    .max(100, "CliQ alias cannot exceed 100 characters")
    .optional()
    .nullable()
    .transform((val) => val ?? undefined),
  cliq_bank_name: z
    .string()
    .max(100, "CliQ bank name cannot exceed 100 characters")
    .optional()
    .nullable()
    .transform((val) => val ?? undefined),
  enable_salon_credits: z
    .boolean()
    .optional()
    .nullable()
    .transform((val) => val ?? true),
  enable_waitlist: z
    .boolean()
    .optional()
    .nullable()
    .transform((val) => val ?? true),
  // Zod strips keys it doesn't declare. This one was never declared, so every
  // business object validated through forensicValidate lost its
  // messaging_settings entirely -- which is why the Retention & Audit tab was
  // permanently invisible in the dashboard even when enabled in SuperAdmin.
  // The server side was always fine (send-whatsapp-v2 reads the column
  // straight from the DB), so this was a client-only blind spot.
  //
  // Kept permissive on purpose: fields stay optional and absent settings are
  // treated as "on" by the dispatcher, matching DEFAULT_MESSAGING_SETTINGS in
  // BusinessForm.tsx. A stricter schema here would silently disable messaging
  // for any business whose JSON predates a later-added key.
  messaging_settings: z
    .object({
      notifications: z
        .object({
          confirmations: z.boolean().optional(),
          reminders: z.boolean().optional(),
          cancellations: z.boolean().optional(),
          reschedules: z.boolean().optional(),
          missed: z.boolean().optional(),
        })
        .optional(),
      retention: z
        .object({
          enabled: z.boolean().optional(),
          multiplier: z.number().optional(),
          auto_nudge: z.boolean().optional(),
          escalation_days: z.number().optional(),
          default_interval_days: z.number().optional(),
          nudge_time: z.string().optional(),
        })
        .optional(),
    })
    .optional()
    .nullable()
    .transform((val) => val ?? undefined),
});

/**
 * Forensic Validation Helper
 * Ensures data matches the schema or fails loudly (Sovereign Principle)
 */
export function forensicValidate<T>(
  schema: z.ZodSchema<T>,
  data: unknown,
  label?: string,
): T {
  try {
    return schema.parse(data);
  } catch (error) {
    // If it's a dev environment, we throw. In production, we log to Sentry (already handled via global errors).
    throw new Error(
      `Data integrity violation [${label || "Unknown"}]: ${error instanceof Error ? error.message : "Unknown schema mismatch"}`,
      { cause: error },
    );
  }
}
